import { usePrompt } from '@ur-solutions/react-components'
import { useSalaryCodeMutations } from './hooks'
import type {
  AllSalaryCodesQuery,
  AllSalaryCodesQueryVariables,
  SalaryCodeNode,
} from '@/modules/companies/types.graphql'
import { useTranslate } from '@ur-solutions/react-hooks'
import { OldAddButton, Header, OldSection, SectionHeader } from '../common'
import { ALL_SALARY_CODES_QUERY } from '@/modules/companies/queries'
import { useQuery } from '@apollo/client'
import { useConfirm } from '@/utils/hooks'
import { SalaryCodePrompt } from './SalaryCodePrompt'
import { DataTable, FAIcon, Input } from '@/components'
import { createColumnHelper } from '@tanstack/react-table'
import { useCallback, useMemo, useState } from 'react'

export interface SalaryCodeForm {
  externalId: string
  name: string
  useForAutoDietGeneration: boolean
  isDiet: boolean
  isSalaryDay: boolean
  rate: number
  showInManualTimeEntryOvertime: boolean
  showInTimeEntries: boolean
}

const column = createColumnHelper<SalaryCodeNode>()

export const SalaryCodeSettings: React.JSX.CFC = () => {
  const translations = useTranslate({
    prompt: {
      delete: ['settings.prompts.delete', { name: '' }],
      deleteTitle: 'settings.prompts.salary-code-delete-title',
    },
    salaryCodes: 'common.salary-codes',
    salaryCode: 'settings.salary-type',
    name: 'common.name',
    noResults: 'common.no-results',
    search: 'common.search',
  })

  const addPrompt = usePrompt()
  const confirm = useConfirm()
  const mutations = useSalaryCodeMutations()

  const { data } = useQuery<AllSalaryCodesQuery, AllSalaryCodesQueryVariables>(
    ALL_SALARY_CODES_QUERY
  )

  const salaryCodes = useMemo(
    () => data?.allSalaryCodes.edges?.map(edge => edge.node) ?? [],
    [data]
  )
  const [search, setSearch] = useState('')

  const filteredSalaryCodes = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase()
    if (!normalizedSearch) return salaryCodes

    return salaryCodes.filter(salaryCode =>
      [salaryCode.name, salaryCode.externalId]
        .filter(Boolean)
        .some(value => value.toLowerCase().includes(normalizedSearch))
    )
  }, [salaryCodes, search])

  async function handleCreateSalaryCode() {
    const { data } = await addPrompt<SalaryCodeForm | null>(resolve => (
      <SalaryCodePrompt existingSalaryCodes={salaryCodes} onSubmit={resolve} />
    ))
    if (data === null) return

    void mutations.createSalaryCode({
      variables: {
        input: {
          ...data,
          showInTimeEntries: data.showInTimeEntries,
        },
      },
    })
  }

  const handlePatchSalaryCode = useCallback(
    async (salaryCode: SalaryCodeNode) => {
      const { data } = await addPrompt<SalaryCodeForm | null>(resolve => (
        <SalaryCodePrompt
          initialData={{
            ...salaryCode,
            showInTimeEntries: salaryCode.showInTimeEntries,
          }}
          existingSalaryCodes={salaryCodes}
          onSubmit={resolve}
        />
      ))
      if (data === null) return

      void mutations.patchSalaryCode({
        variables: {
          id: salaryCode.id,
          input: {
            externalId: data.externalId,
            name: data.name,
            isDiet: data.isDiet,
            useForAutoDietGeneration: data.useForAutoDietGeneration,
            isSalaryDay: data.isSalaryDay,
            rate: data.rate,
            showInManualTimeEntryOvertime: data.showInManualTimeEntryOvertime,
            showInTimeEntries: data.showInTimeEntries,
          },
        },
      })
    },
    [addPrompt, mutations, salaryCodes]
  )

  const handleDeleteSalaryCode = useCallback(
    async (salaryCode: SalaryCodeNode) => {
      const { data: answer } = await confirm(
        translations.prompt.delete({ name: salaryCode.name }),
        translations.prompt.deleteTitle
      )
      if (!answer) return

      void mutations.deleteSalaryCode({
        variables: {
          id: salaryCode.id,
        },
      })
    },
    [confirm, mutations, translations.prompt]
  )

  const tableColumns = useMemo(
    () => [
      column.accessor('name', {
        header: translations.name,
        enableSorting: true,
      }),
      column.accessor('externalId', {
        header: translations.salaryCode,
        enableSorting: true,
      }),
      column.display({
        id: 'edit',
        meta: {
          narrow: true,
        },
        cell(info) {
          const salaryCode = info.row.original

          return (
            <FAIcon
              className="text-gray-400 hover:text-secondary"
              icon="edit"
              cursor="pointer"
              onClick={() => handlePatchSalaryCode(salaryCode)}
            />
          )
        },
      }),
      column.display({
        id: 'delete',
        meta: {
          narrow: true,
        },
        cell(info) {
          const salaryCode = info.row.original

          return (
            <FAIcon
              className="text-gray-400 hover:text-red-500"
              icon="times"
              cursor="pointer"
              onClick={() => handleDeleteSalaryCode(salaryCode)}
            />
          )
        },
      }),
    ],
    [
      handleDeleteSalaryCode,
      handlePatchSalaryCode,
      translations.name,
      translations.salaryCode,
    ]
  )

  return (
    <OldSection>
      <Header>
        <SectionHeader>{translations.salaryCodes}</SectionHeader>
        <OldAddButton adding={false} onClick={handleCreateSalaryCode} />
      </Header>

      <div className="mb-4 max-w-sm">
        <Input
          aria-label={translations.search}
          placeholder={translations.search}
          type="search"
          value={search}
          onChange={setSearch}
        />
      </div>

      <DataTable
        columns={tableColumns}
        data={filteredSalaryCodes}
        noDataText={translations.noResults}
        pagination={{ pageSize: 20 }}
      />
    </OldSection>
  )
}
